import { HttpClient } from '@angular/common/http';
import {Injectable, Injector, inject, PLATFORM_ID} from '@angular/core';
import { AppSurfaceService } from '@core/config/app-surface.service';
import { UserModel } from '@core/store/user/user.model';
import { UserStore } from '@core/store/user/user.store';
import { environment } from '@environments/environment';
import { API_ROOT } from '@shared/constants/api-root.const';
import { CookieService } from '@shared/services/cookie.service';
import { AuthService } from '@zitemedia/zitadel-sso-frontend';
import {catchError, map} from 'rxjs/operators';
import {EMPTY, Observable, throwError} from 'rxjs';
import {isPlatformBrowser} from '@angular/common';

@Injectable({providedIn: 'root'})
export class UserService {
  private _API_ROOT = inject(API_ROOT);
  private _http = inject(HttpClient);
  private _cookieService = inject(CookieService);
  private _userStore = inject(UserStore);
  private _injector = inject(Injector);
  private platformId = inject(PLATFORM_ID);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {
  }

  loadUser(): Observable<Object> {
    if (!isPlatformBrowser(this.platformId)) {
      // On the server, avoid HTTP calls and return a completed observable to keep SSR safe
      return EMPTY as Observable<Object>;
    }
    return this._http.get(`${this._API_ROOT}users/me/`);
  }

  /**
   * Perform backend login with service account to obtain a fresh API key.
   * Backend endpoint: POST /api/auth/login/
   * Expects { username, password } and returns user object with apiKey field.
   */
  login(credentials: { username: string; password: string }): Observable<string> {
    return this._http.post<any>(`${this._API_ROOT}auth/login/`, credentials).pipe(
      map((resp) => {
        const key = resp?.apiKey || resp?.api_key;
        if (!key) {
          throw new Error('API key is missing in login response');
        }
        return key as string;
      }),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  getUser() {
    return this.loadUser()
      .subscribe(
        (body: any) => {
          this.setUser(body);
          this._userStore.setHasCache(true);
        },
        (err) => {
          this._userStore.update({isLoggedIn: false});
          this._userStore.setError(true);
          if (err.status === 401 || err.status === 403) {
            // `logout()` ends in `window.location.assign`, and
            // `appInitializerFactory` calls this on every NavigationStart for
            // which a cookie exists. The auth cookie is written with a ~10-year
            // maxAge while its token expires in hours, so a returning visitor
            // very often has one without a session.
            //
            // On the estimator that hard navigation is right: every page there
            // needs an account, so there is nothing to stay for. On the
            // briefings host there is -- the whole point of that host is that a
            // shared link opens without one. Bouncing the reader to a logout
            // URL is the exact failure the public endpoints exist to avoid, so
            // the stale cookie is dropped and the page continues signed out.
            const surface = this._injector.get(AppSurfaceService);
            if (surface.current === 'briefings') {
              this._cookieService.deleteCookie(environment.authCookieName);
              return;
            }
            const authService = this._injector.get(AuthService);
            authService.logout();
          }
        }
      );
  }

  updateUserHandlers(val) {
    this._userStore.update((state) => {
      return {
        ...state,
        user: {
          ...state.user,
          user_default_handlers: [...val]
        }
      };
    });
  }

  logout() {
    // This method is kept for backward compatibility
    // Perform local cleanup
    this._cookieService.deleteCookie('PHPSESSID');
    this._cookieService.deleteCookie(environment.authCookieName);
    this._userStore.resetUser();
    this._userStore.setHasCache(false);

    // Get AuthService using injector to avoid circular dependency
    const authService = this._injector.get(AuthService);

    // Call AuthService.logout() directly
    return authService.logout();
  }

  setUser(user: UserModel) {
    this._userStore.update({user, isLoggedIn: true});
  }
}
